import { useEffect, useRef, useState } from "react";
import { Search, UserPlus, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RequiredPatientFieldsFields from "@/components/patients/RequiredPatientFieldsFields";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AGENDA, PATIENTS } from "@/constants/testIds";

const EMPTY_NEW_PATIENT = { name: "", phone: "", email: "", date_of_birth: "", emergency_contact_name: "", emergency_contact_phone: "", allergies: "" };

const REQUIRED_FIELD_TEST_IDS = {
  dob: PATIENTS.quickAddDobInput,
  emergencyName: PATIENTS.quickAddEmergencyNameInput,
  emergencyPhone: PATIENTS.quickAddEmergencyPhoneInput,
  allergies: PATIENTS.quickAddAllergiesInput,
};

/**
 * Busca un paciente existente por nombre/teléfono/email, o permite dar de
 * alta uno nuevo (nombre y teléfono siempre obligatorios, más los campos que
 * la clínica marcó como obligatorios en Configuración).
 */
export default function PatientPicker({ value, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newPatient, setNewPatient] = useState(EMPTY_NEW_PATIENT);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [requiredFields, setRequiredFields] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/clinic").then(({ data }) => setRequiredFields(data.required_patient_fields || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (value || !query.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { search: query.trim() } });
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, value]);

  const handleCreate = async () => {
    if (newPatient.name.trim().length < 2) {
      setError("El nombre debe tener al menos 2 caracteres");
      return;
    }
    if (newPatient.phone.trim().length < 6) {
      setError("El teléfono es obligatorio");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const payload = { name: newPatient.name.trim(), phone: newPatient.phone.trim() };
      if (newPatient.email.trim()) payload.email = newPatient.email.trim();
      if (newPatient.date_of_birth) payload.date_of_birth = newPatient.date_of_birth;
      if (newPatient.emergency_contact_name?.trim()) payload.emergency_contact_name = newPatient.emergency_contact_name.trim();
      if (newPatient.emergency_contact_phone?.trim()) payload.emergency_contact_phone = newPatient.emergency_contact_phone.trim();
      if (newPatient.allergies?.trim()) payload.allergies = newPatient.allergies.trim();
      const { data } = await api.post("/patients", payload);
      onSelect(data);
      setShowCreate(false);
      setNewPatient(EMPTY_NEW_PATIENT);
      setQuery("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  if (value) {
    return (
      <div
        className="flex items-center justify-between gap-2 rounded-lg border border-brand-light bg-brand-light/40 px-3 py-2"
        data-testid={AGENDA.modalSelectedPatientBadge}
      >
        <div className="text-sm">
          <span className="font-medium text-foreground">{value.name}</span>
          {value.phone && <span className="text-muted-foreground"> &middot; {value.phone}</span>}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSelect(null)}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Buscar paciente por nombre o teléfono..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid={AGENDA.modalPatientSearchInput}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {results.length > 0 && (
        <div className="border border-border rounded-lg divide-y divide-border max-h-40 overflow-y-auto">
          {results.map((p) => (
            <button
              type="button"
              key={p.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-background transition-colors"
              onClick={() => { onSelect(p); setQuery(""); setResults([]); }}
              data-testid={`${AGENDA.modalPatientOptionPrefix}${p.id}`}
            >
              <span className="font-medium text-foreground">{p.name}</span>
              {p.phone && <span className="text-muted-foreground"> &middot; {p.phone}</span>}
            </button>
          ))}
        </div>
      )}

      {!showCreate ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full justify-center gap-2"
          onClick={() => setShowCreate(true)}
          data-testid={AGENDA.modalPatientCreateToggle}
        >
          <UserPlus className="w-3.5 h-3.5" /> Crear paciente nuevo
        </Button>
      ) : (
        <div className="border border-border rounded-lg p-3 space-y-2 bg-background">
          <Input
            placeholder="Nombre completo *"
            value={newPatient.name}
            onChange={(e) => setNewPatient((f) => ({ ...f, name: e.target.value }))}
            data-testid={AGENDA.modalPatientNewNameInput}
          />
          <Input
            placeholder="Teléfono *"
            value={newPatient.phone}
            onChange={(e) => setNewPatient((f) => ({ ...f, phone: e.target.value }))}
            data-testid={AGENDA.modalPatientNewPhoneInput}
          />
          <Input
            placeholder={requiredFields.includes("email") ? "Email *" : "Email (opcional)"}
            value={newPatient.email}
            onChange={(e) => setNewPatient((f) => ({ ...f, email: e.target.value }))}
            data-testid={AGENDA.modalPatientNewEmailInput}
          />
          <RequiredPatientFieldsFields requiredFields={requiredFields} form={newPatient} setForm={setNewPatient} testIds={REQUIRED_FIELD_TEST_IDS} />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCreate(false); setError(""); }}>Cancelar</Button>
            <Button type="button" size="sm" disabled={creating} onClick={handleCreate} data-testid={AGENDA.modalPatientCreateButton}>
              {creating ? "Creando..." : "Crear y usar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
